import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import {
  saveCatalog, getCatalog, savePendingVisit,
  saveDraft, getDraft, updateDraft, deleteDraft,
} from '../../services/localDB';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import Layout from '../../components/Layout';
import MeterField from '../../components/MeterField';
import styles from './NewVisit.module.css';

const STEPS = ['Ubicación', 'Medidores', 'Observaciones'];
const GPS_KEY = 'lectura-ia-last-gps';

function getLastGPS() {
  try {
    const raw = localStorage.getItem(GPS_KEY);
    if (!raw) return null;
    const { lat, lng, ts } = JSON.parse(raw);
    const age = Date.now() - ts;
    if (age > 2 * 60 * 60 * 1000) return null;
    return { lat, lng, age };
  } catch { return null; }
}

function saveLastGPS(lat, lng) {
  localStorage.setItem(GPS_KEY, JSON.stringify({ lat, lng, ts: Date.now() }));
}

function formatGPSAge(ms) {
  const min = Math.round(ms / 60000);
  if (min < 2) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  return `hace ${Math.round(min / 60)}h`;
}

const EMPTY_MEDIDOR = {
  foto: null, preview: null, lectura: '', foto_path: null,
  foto_file: null, sin_acceso: false, motivo_sin_acceso: null,
  lectura_guardada: false,
};

export default function NewVisit() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const online        = useOnlineStatus();

  // URL param: ?draft=<id> para continuar un borrador
  const urlDraftId = searchParams.get('draft');

  const [step, setStep]               = useState(0);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [savedOffline, setSavedOffline] = useState(false);

  // Estado de borrador
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [locationLocked, setLocationLocked]  = useState(false); // true al continuar borrador
  const autoSaveTimer  = useRef(null);
  const horaInicioRef  = useRef(new Date().toISOString()); // capturado al montar

  // Catálogos
  const [ciudades, setCiudades]         = useState([]);
  const [conjuntos, setConjuntos]       = useState([]);
  const [torres, setTorres]             = useState([]);
  const [allConjuntos, setAllConjuntos] = useState([]);
  const [allTorres, setAllTorres]       = useState([]);

  // Paso 0
  const [latitud, setLatitud]           = useState('');
  const [longitud, setLongitud]         = useState('');
  const [gpsLoading, setGpsLoading]     = useState(false);
  const [gpsMode, setGpsMode]           = useState(null);
  const [lastGPS, setLastGPS]           = useState(null);
  const [ciudadId, setCiudadId]         = useState('');
  const [conjuntoId, setConjuntoId]     = useState('');
  const [torreId, setTorreId]           = useState('');
  const [apartamento, setApartamento]   = useState('');

  // Duplicado
  const [duplicado, setDuplicado]               = useState(null);
  const [duplicadoIgnorado, setDuplicadoIgnorado] = useState(false);

  // Medidores
  const [medidores, setMedidores] = useState({
    luz:  { ...EMPTY_MEDIDOR },
    agua: { ...EMPTY_MEDIDOR },
    gas:  { ...EMPTY_MEDIDOR },
  });

  const [observaciones, setObservaciones] = useState('');

  // ── Catálogos ─────────────────────────────────────────────────────
  const loadCatalogos = useCallback(async () => {
    if (online) {
      try {
        const [ciRes, cjRes, tRes] = await Promise.all([
          api.get('/catalogs/ciudades'),
          api.get('/catalogs/conjuntos/all').catch(() => ({ data: [] })),
          api.get('/catalogs/torres/all').catch(() => ({ data: [] })),
        ]);
        setCiudades(ciRes.data);
        setAllConjuntos(cjRes.data);
        setAllTorres(tRes.data);
        await Promise.all([
          saveCatalog('ciudades',      ciRes.data),
          saveCatalog('conjuntos_all', cjRes.data),
          saveCatalog('torres_all',    tRes.data),
        ]);
      } catch {
        await loadFromCache();
      }
    } else {
      await loadFromCache();
    }
  }, [online]); // eslint-disable-line

  const loadFromCache = async () => {
    const [ci, cj, to] = await Promise.all([
      getCatalog('ciudades'),
      getCatalog('conjuntos_all'),
      getCatalog('torres_all'),
    ]);
    if (ci) setCiudades(ci);
    if (cj) setAllConjuntos(cj);
    if (to) setAllTorres(to);
  };

  useEffect(() => { loadCatalogos(); }, [loadCatalogos]);

  // Filtrar conjuntos y torres — SOLO actualiza la lista del dropdown,
  // NO resetea el ID seleccionado. Los resets se hacen únicamente en los
  // onChange handlers para evitar borrar los IDs del borrador cuando los
  // catálogos cargan asincrónicamente en paralelo con la carga del draft.
  useEffect(() => {
    if (!ciudadId) { setConjuntos([]); return; }
    if (online) {
      api.get(`/catalogs/conjuntos?ciudad_id=${ciudadId}`)
        .then(r => setConjuntos(r.data))
        .catch(() => setConjuntos(allConjuntos.filter(c => String(c.ciudad_id) === String(ciudadId))));
    } else {
      setConjuntos(allConjuntos.filter(c => String(c.ciudad_id) === String(ciudadId)));
    }
  }, [ciudadId, online, allConjuntos]); // eslint-disable-line

  useEffect(() => {
    if (!conjuntoId) { setTorres([]); return; }
    if (online) {
      api.get(`/catalogs/torres?conjunto_id=${conjuntoId}`)
        .then(r => setTorres(r.data))
        .catch(() => setTorres(allTorres.filter(t => String(t.conjunto_id) === String(conjuntoId))));
    } else {
      setTorres(allTorres.filter(t => String(t.conjunto_id) === String(conjuntoId)));
    }
  }, [conjuntoId, online, allTorres]); // eslint-disable-line

  // Handlers de selección manual — resetean dependientes explícitamente
  const handleCiudadChange = (value) => {
    setCiudadId(value);
    setConjuntoId('');
    setTorreId('');
  };
  const handleConjuntoChange = (value) => {
    setConjuntoId(value);
    setTorreId('');
  };

  // ── Cargar borrador (si viene con ?draft=id) ───────────────────────
  useEffect(() => {
    if (!urlDraftId) return;
    loadDraftById(urlDraftId);
  }, []); // eslint-disable-line

  const loadDraftById = async (id) => {
    try {
      const draft = await getDraft(id);
      if (!draft) return; // borrador no encontrado, flujo normal

      // Restaurar ubicación
      setCiudadId(draft.ciudadId     || '');
      setConjuntoId(draft.conjuntoId || '');
      setTorreId(draft.torreId       || '');
      setApartamento(draft.apartamento || '');
      setLatitud(draft.latitud         || '');
      setLongitud(draft.longitud        || '');
      setGpsMode(draft.gpsMode         || null);
      setObservaciones(draft.observaciones || '');

      // Restaurar medidores — recrear preview desde foto_file
      if (draft.medidores) {
        const restored = {};
        for (const tipo of ['luz', 'agua', 'gas']) {
          const m = draft.medidores[tipo] || { ...EMPTY_MEDIDOR };
          // Prioridad: URL servidor > foto_file > foto (campo legacy) > base64
          let preview = null;
          if (m.foto_path) {
            preview = `/uploads/${m.foto_path}`;
          } else if (m.foto_file) {
            preview = URL.createObjectURL(m.foto_file);
          } else if (m.foto) {
            preview = URL.createObjectURL(m.foto);
          } else if (m.foto_base64) {
            preview = m.foto_base64; // data URL funciona directamente como src
          }
          restored[tipo] = { ...m, preview };
        }
        setMedidores(restored);
      }

      setCurrentDraftId(id);
      setLocationLocked(true);
      setStep(1); // saltar paso 0: ubicación ya está configurada
    } catch (err) {
      console.error('Error cargando borrador:', err);
    }
  };

  // ── Ref con el payload más reciente del borrador ──────────────────────────
  // Permite saves de emergencia (foto capturada, app yendo al fondo) sin
  // closures estale ni tener que re-leer todo el estado.
  const draftPayloadRef = useRef(null);

  // ── Auto-guardado del borrador ─────────────────────────────────────────
  // Se dispara cuando cambia cualquier dato relevante y hay un draft activo.
  // Debounce de 800ms para cambios de texto; fotos se guardan inmediatamente
  // en handleMedidorFile (ver abajo).
  useEffect(() => {
    if (!currentDraftId) return;

    // Quitar solo preview (blob URL no sobrevive IDB).
    // foto_file (File/Blob) SÍ es serializable en IDB — no lo eliminamos.
    const medidoresForSave = {};
    for (const tipo of ['luz', 'agua', 'gas']) {
      medidoresForSave[tipo] = { ...medidores[tipo], preview: null };
    }

    const conjuntoNombre =
      conjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre ||
      allConjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre || '';
    const torreNombre =
      torres.find(t => String(t.id) === String(torreId))?.nombre ||
      allTorres.find(t => String(t.id) === String(torreId))?.nombre || '';

    const payload = {
      ciudadId, conjuntoId, torreId, apartamento,
      latitud, longitud, gpsMode,
      observaciones,
      medidores: medidoresForSave,
      _meta: {
        ciudadNombre: ciudades.find(c => String(c.id) === String(ciudadId))?.nombre || '',
        conjuntoNombre,
        torreNombre,
      },
    };

    // Siempre mantener el ref fresco — lo usan los saves de emergencia
    draftPayloadRef.current = payload;

    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try { await updateDraft(currentDraftId, payload); }
      catch { /* fallo silencioso — el borrador se guardó antes */ }
    }, 800);

    return () => clearTimeout(autoSaveTimer.current);
  }, [ // eslint-disable-line
    currentDraftId,
    ciudadId, conjuntoId, torreId, apartamento,
    latitud, longitud, gpsMode,
    medidores, observaciones,
  ]);

  // ── Guardar borrador al ir al fondo ────────────────────────────────────
  // iOS Safari (y algunos Android) puede matar la PWA al cambiar de app.
  // visibilitychange dispara ANTES de que el proceso sea eliminado.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && currentDraftId && draftPayloadRef.current) {
        // Fire-and-forget — no podemos await en un listener síncrono
        updateDraft(currentDraftId, draftPayloadRef.current).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentDraftId]); // eslint-disable-line

  // Scroll al top en cada cambio de paso
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [step]);

  // Último GPS conocido
  useEffect(() => { setLastGPS(getLastGPS()); }, []);

  // ── GPS ───────────────────────────────────────────────────────────
  const getGPS = () => {
    setGpsLoading(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        setLatitud(lat);
        setLongitud(lng);
        setGpsMode('direct');
        saveLastGPS(lat, lng);
        setLastGPS(getLastGPS());
        setGpsLoading(false);
      },
      () => {
        setGpsLoading(false);
        const last = getLastGPS();
        if (last) {
          setLastGPS(last);
        } else {
          setError('No se pudo obtener GPS. Intenta desde la entrada del edificio.');
        }
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const useLastGPS = () => {
    if (!lastGPS) return;
    setLatitud(lastGPS.lat);
    setLongitud(lastGPS.lng);
    setGpsMode('last_known');
  };

  // ── Medidores ─────────────────────────────────────────────────────
  const updateMedidor = (tipo, field, value) => {
    setMedidores(prev => ({ ...prev, [tipo]: { ...prev[tipo], [field]: value } }));
  };

  // Ref que rastrea la operación async de foto en curso.
  // handleSaveDraft lo espera para no guardar el borrador con foto_file/base64 aún null.
  const pendingPhotoSaveRef = useRef(null);

  const handleMedidorFile = (tipo, file) => {
    // ── Crear la Promise ANTES de cualquier await ──────────────────────
    // Si el auditor pulsa "Guardar y volver" mientras el base64 aún se computa,
    // handleSaveDraft puede detectar la operación en curso y esperarla.
    const operation = (async () => {
      let base64 = null;
      try {
        base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } catch { /* base64 queda null, se usará foto_file como fallback */ }

      setMedidores(prev => ({ ...prev, [tipo]: { ...prev[tipo], foto_file: file, foto_base64: base64 } }));

      // ── Guardado inmediato en IDB ──────────────────────────────────────
      // No esperar el debounce de 800ms para que la foto persista incluso si
      // el SO mata la app antes de que el auto-save dispare.
      if (currentDraftId && draftPayloadRef.current) {
        const prevPayload = draftPayloadRef.current;
        const updatedPayload = {
          ...prevPayload,
          medidores: {
            ...prevPayload.medidores,
            [tipo]: { ...prevPayload.medidores[tipo], foto_file: file, foto_base64: base64, preview: null },
          },
        };
        draftPayloadRef.current = updatedPayload;
        await updateDraft(currentDraftId, updatedPayload).catch(() => {});
      }
    })();

    // Registrar en ref INMEDIATAMENTE (antes del primer await de la op)
    pendingPhotoSaveRef.current = operation;
    operation.finally(() => {
      // Limpiar solo si este sigue siendo el save pendiente activo
      if (pendingPhotoSaveRef.current === operation) pendingPhotoSaveRef.current = null;
    });
  };

  // Estado de cada medidor
  const estadoMedidor = (t) => {
    const m = medidores[t];
    const tieneFoto = m.foto_file || m.foto_path || m.foto_base64 || m.preview;
    if (m.sin_acceso)                          return 'sinAcceso';   // ✓ OK
    if (tieneFoto && m.lectura && m.lectura_guardada) return 'completo'; // ✓ OK
    if (tieneFoto && !(m.lectura && m.lectura_guardada)) return 'sinLectura'; // foto sin lectura confirmada
    return 'pendiente'; // sin foto y sin sin_acceso
  };

  // Medidores incompletos (cualquier razón)
  const medidoresPendientes = ['luz', 'agua', 'gas'].filter(
    t => !['completo', 'sinAcceso'].includes(estadoMedidor(t))
  );

  const canNext = () => {
    if (step === 0) return ciudadId && conjuntoId && apartamento.trim() && latitud;
    if (step === 1) return medidoresPendientes.length === 0;
    return true;
  };

  // ── Avanzar paso ──────────────────────────────────────────────────
  const handleNext = async (skipDuplicateCheck = false) => {
    setError('');

    if (step === 0) {
      // Verificar duplicado si online (saltar si el usuario ya confirmó continuar)
      if (online && !skipDuplicateCheck) {
        try {
          const params = new URLSearchParams({
            conjunto_id: conjuntoId,
            apartamento: apartamento.trim(),
            ...(torreId ? { torre_id: torreId } : {}),
          });
          const { data } = await api.get(`/visits/check-duplicate?${params}`);
          if (data.count > 0) { setDuplicado(data); return; }
        } catch { /* si falla la verificación, continuar */ }
      }
      setDuplicado(null);
      setDuplicadoIgnorado(false);

      // Crear borrador al entrar al paso de medidores
      if (!currentDraftId) {
        try {
          const medidoresForSave = {};
          for (const tipo of ['luz', 'agua', 'gas']) {
            medidoresForSave[tipo] = { ...medidores[tipo], preview: null, foto_file: null };
          }
          const id = await saveDraft({
            ciudadId, conjuntoId, torreId, apartamento,
            latitud, longitud, gpsMode,
            observaciones,
            medidores: medidoresForSave,
            _meta: {
              ciudadNombre:   ciudades.find(c => String(c.id) === String(ciudadId))?.nombre || '',
              conjuntoNombre: conjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre || '',
              torreNombre:    torres.find(t => String(t.id) === String(torreId))?.nombre || '',
            },
          });
          setCurrentDraftId(id);
        } catch { /* fallo no crítico */ }
      }
    }

    setStep(s => s + 1);
  };

  // ── Guardar progreso y salir (vuelve a Mis Visitas) ───────────────
  const handleSaveDraft = async () => {
    clearTimeout(autoSaveTimer.current);

    // Esperar a que termine el guardado de foto si está en curso.
    // Sin esto, si el auditor toma una foto y pulsa "Guardar" inmediatamente
    // (~400 ms de conversión base64), el draft se graba con foto_file/base64=null
    // y la foto desaparece al reabrir el borrador.
    if (pendingPhotoSaveRef.current) {
      await pendingPhotoSaveRef.current;
    }

    if (currentDraftId) {
      // draftPayloadRef.current ya refleja los datos más recientes — incluyendo las
      // fotos que acaba de resolver pendingPhotoSaveRef. Úsalo como fuente de verdad.
      if (draftPayloadRef.current) {
        await updateDraft(currentDraftId, draftPayloadRef.current).catch(() => {});
      } else {
        // Fallback: construir payload desde estado (ruta de primera vez sin ref listo)
        const medidoresForSave = {};
        for (const tipo of ['luz', 'agua', 'gas']) {
          medidoresForSave[tipo] = { ...medidores[tipo], preview: null };
        }
        const conjuntoNombreGuard =
          conjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre ||
          allConjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre || '';
        const torreNombreGuard =
          torres.find(t => String(t.id) === String(torreId))?.nombre ||
          allTorres.find(t => String(t.id) === String(torreId))?.nombre || '';
        await updateDraft(currentDraftId, {
          ciudadId, conjuntoId, torreId, apartamento,
          latitud, longitud, gpsMode,
          observaciones,
          medidores: medidoresForSave,
          _meta: {
            ciudadNombre:   ciudades.find(c => String(c.id) === String(ciudadId))?.nombre || '',
            conjuntoNombre: conjuntoNombreGuard,
            torreNombre:    torreNombreGuard,
          },
        }).catch(() => {});
      }
    }
    navigate('/mis-visitas');
  };

  // ── Guardar visita final ───────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    // Guardia pre-envío: evita llamar al backend con datos incompletos
    if (!ciudadId || !conjuntoId || !apartamento.trim()) {
      setError('Faltan datos de ubicación (ciudad o conjunto). Presiona "Editar ubicación" en el banner para corregirlos.');
      setSubmitting(false);
      return;
    }

    const medidoresPayload = ['luz', 'agua', 'gas'].reduce((acc, tipo) => {
      const m = medidores[tipo];
      if (m.foto_path || m.lectura || m.sin_acceso || m.foto_file || m.foto_base64) {
        acc[tipo] = {
          // Si hay sin_acceso no se guarda foto (limpia cualquier captura previa del mismo paso)
          foto_path:         m.sin_acceso ? null : (m.foto_path  || null),
          foto_file:         m.sin_acceso ? null : (m.foto_file  || null),
          foto_base64:       m.sin_acceso ? null : (m.foto_base64 || null),
          lectura:           m.lectura           || null,
          sin_acceso:        m.sin_acceso        || false,
          motivo_sin_acceso: m.motivo_sin_acceso || null,
          hora_foto:         m.sin_acceso ? null : (m.hora_foto  || null),
        };
      }
      return acc;
    }, {});

    const cleanDraft = async () => {
      if (currentDraftId) await deleteDraft(currentDraftId).catch(() => {});
    };

    // Persiste como visita pendiente en IDB (offline o fallback de visita mixta).
    // Las fotos con foto_path (ya subidas) son CASO A en sync — se reusan.
    // Las fotos con foto_base64 son CASO B — se suben al sincronizar.
    const guardarOffline = async () => {
      const medidoresParaIDB = {};
      for (const [tipo, m] of Object.entries(medidoresPayload)) {
        let base64 = m.foto_base64 || null;
        if (m?.foto_file && !base64) {
          base64 = await new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload  = () => res(reader.result);
            reader.onerror = rej;
            reader.readAsDataURL(m.foto_file);
          }).catch(() => null);
        }
        medidoresParaIDB[tipo] = { ...m, foto_file: null, foto_base64: base64 };
      }
      await savePendingVisit({
        latitud, longitud,
        ciudad_id:   ciudadId,
        conjunto_id: conjuntoId,
        torre_id:    torreId || null,
        apartamento, observaciones,
        medidores:   medidoresParaIDB,
        hora_inicio: horaInicioRef.current,
        hora_fin:    new Date().toISOString(),
        _meta: {
          ciudadNombre:   ciudades.find(c => String(c.id) === String(ciudadId))?.nombre || '',
          conjuntoNombre: conjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre || '',
          torreNombre:    torres.find(t => String(t.id) === String(torreId))?.nombre || '',
        },
      });
      await cleanDraft();
      setSavedOffline(true);
    };

    if (online) {
      // Intentar subir fotos pendientes (visita mixta: algunas online, otras offline)
      let todosSubidos = true;
      for (const tipo of ['luz', 'agua', 'gas']) {
        const m = medidoresPayload[tipo];
        if (!m || m.foto_path || (!m.foto_file && !m.foto_base64)) continue;
        try {
          const formData = new FormData();
          if (m.foto_base64) {
            const blob = await fetch(m.foto_base64).then(r => r.blob());
            formData.append('foto', blob, `${tipo}_${Date.now()}.jpg`);
          } else {
            formData.append('foto', m.foto_file, `${tipo}_${Date.now()}.jpg`);
          }
          const { data: uploaded } = await api.post('/visits/upload-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          m.foto_path = uploaded.foto_path;
        } catch {
          // Falló el upload — ya no hay conexión real; guardar todo offline
          todosSubidos = false;
          break;
        }
      }

      if (!todosSubidos) {
        // Caída a offline: fotos con foto_path (CASO A) + foto_base64 (CASO B) → sync las resolverá
        try { await guardarOffline(); } catch { setError('Error al guardar localmente'); setSubmitting(false); }
        return;
      }

      try {
        // Enviar solo los campos que el servidor necesita — sin base64 ni File objects
        const medidoresParaServidor = {};
        for (const [tipo, m] of Object.entries(medidoresPayload)) {
          if (!m) continue;
          medidoresParaServidor[tipo] = {
            foto_path:         m.sin_acceso ? null : (m.foto_path || null),
            lectura:           m.lectura           || null,
            sin_acceso:        m.sin_acceso        || false,
            motivo_sin_acceso: m.motivo_sin_acceso || null,
            hora_foto:         m.sin_acceso ? null : (m.hora_foto || null),
          };
        }
        await api.post('/visits', {
          latitud, longitud,
          ciudad_id:   ciudadId,
          conjunto_id: conjuntoId,
          torre_id:    torreId || null,
          apartamento, observaciones,
          medidores:   medidoresParaServidor,
          hora_inicio: horaInicioRef.current,
          hora_fin:    new Date().toISOString(),
        });
        await cleanDraft();
        navigate('/mis-visitas');
      } catch (err) {
        if (!err.response) {
          // Error de red después de subir fotos → guardar offline con foto_path ya obtenidos
          try { await guardarOffline(); } catch { setError('Error al guardar localmente'); setSubmitting(false); }
        } else {
          setError(err.response?.data?.error || 'Error al guardar la visita');
          setSubmitting(false);
        }
      }
    } else {
      try {
        await guardarOffline();
      } catch {
        setError('Error al guardar localmente');
        setSubmitting(false);
      }
    }
  };

  // ── Helpers de display ────────────────────────────────────────────
  const conjuntoNombre = conjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre
                      || allConjuntos.find(c => String(c.id) === String(conjuntoId))?.nombre || '';
  const torreNombre    = torres.find(t => String(t.id) === String(torreId))?.nombre
                      || allTorres.find(t => String(t.id) === String(torreId))?.nombre || '';

  const meterDoneCount = ['luz', 'agua', 'gas'].filter(t => {
    const m = medidores[t];
    return m.sin_acceso || ((m.lectura && m.lectura_guardada) && (m.foto_path || m.foto_base64 || m.foto_file || m.preview));
  }).length;

  // ── Pantalla éxito offline ─────────────────────────────────────────
  if (savedOffline) {
    return (
      <Layout title="Visita guardada" back="/mis-visitas">
        <div className={styles.offlineSuccess}>
          <span className={styles.offlineSuccessIcon}>📥</span>
          <h2>Guardado localmente</h2>
          <p>
            La visita se sincronizará automáticamente cuando recuperes conexión.
            Puedes verla en "Mis Visitas" con el badge <strong>Por sincronizar</strong>.
          </p>
          <button className={styles.btnPrimary} onClick={() => navigate('/mis-visitas')}>
            Ver mis visitas
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Nueva Visita" back="/">
      {/* Progreso */}
      <div className={styles.progress}>
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`${styles.step} ${i <= step ? styles.active : ''} ${i < step ? styles.done : ''}`}
          >
            <div className={styles.stepDot}>{i < step ? '✓' : i + 1}</div>
            <span className={styles.stepLabel}>{s}</span>
          </div>
        ))}
      </div>

      {/* Banner de borrador (continuar visita) */}
      {locationLocked && (
        <div className={styles.draftBanner}>
          <div className={styles.draftBannerInfo}>
            <span className={styles.draftBannerTitle}>
              📝 Continuando borrador — Apto <strong>{apartamento}</strong>
              {torreNombre ? ` · Torre ${torreNombre}` : ''}
              {conjuntoNombre ? ` · ${conjuntoNombre}` : ''}
            </span>
            <span className={styles.draftBannerGps}>
              📍 {latitud ? `${latitud}, ${longitud}${gpsMode === 'last_known' ? ' (últ. conocida)' : ''}` : '⚠️ Sin GPS'}
            </span>
          </div>
          <button
            className={styles.draftBannerGpsBtn}
            onClick={getGPS}
            disabled={gpsLoading}
          >
            {gpsLoading ? '⏳' : '🔄 GPS'}
          </button>
        </div>
      )}

      {/* Aviso de recuperación: borrador con datos de ubicación incompletos */}
      {locationLocked && (!conjuntoId || !ciudadId) && (
        <div className={styles.ubicacionIncompleta}>
          <span>⚠️ Este borrador perdió datos de ubicación (ciudad o conjunto). Debes corregirlos antes de guardar.</span>
          <button
            className={styles.btnEditarUbicacion}
            onClick={() => { setLocationLocked(false); setStep(0); }}
          >
            ✏️ Editar ubicación
          </button>
        </div>
      )}

      {/* ── PASO 0: Ubicación ─────────────────────────────────────── */}
      {step === 0 && (
        <div className={styles.section}>
          <div className={styles.gpsRow}>
            <button className={styles.gpsBtn} onClick={getGPS} disabled={gpsLoading}>
              {gpsLoading ? '⏳ Obteniendo...' : '📍 Capturar ubicación GPS'}
            </button>
            {latitud && gpsMode === 'direct' && (
              <span className={styles.gpsOk}>✓ {latitud}, {longitud}</span>
            )}
            {latitud && gpsMode === 'last_known' && (
              <span className={styles.gpsWarn}>
                📍 Última ubicación ({formatGPSAge(lastGPS?.age || 0)})
              </span>
            )}
          </div>

          {!latitud && lastGPS && (
            <div className={styles.lastGpsBox}>
              <p>No se detectó GPS aquí. ¿Usar la última ubicación capturada ({formatGPSAge(lastGPS.age)})?</p>
              <button className={styles.btnLastGps} onClick={useLastGPS}>
                Usar última ubicación conocida
              </button>
            </div>
          )}

          {!latitud && (
            <div className={styles.gpsRequired}>
              📍 La georreferenciación es obligatoria — presiona "Capturar ubicación GPS" antes de continuar.
            </div>
          )}

          <div className={styles.field}>
            <label>Ciudad *</label>
            <select value={ciudadId} onChange={e => handleCiudadChange(e.target.value)}>
              <option value="">Seleccionar ciudad</option>
              {ciudades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>Conjunto *</label>
            <select value={conjuntoId} onChange={e => handleConjuntoChange(e.target.value)} disabled={!ciudadId}>
              <option value="">Seleccionar conjunto</option>
              {conjuntos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>Torre</label>
            <select value={torreId} onChange={e => setTorreId(e.target.value)} disabled={!conjuntoId}>
              <option value="">Sin torre / No aplica</option>
              {torres.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>Apartamento *</label>
            <input
              type="text"
              value={apartamento}
              onChange={e => { setApartamento(e.target.value); setDuplicado(null); }}
              placeholder="Ej: 401, 12B"
              maxLength={20}
            />
          </div>

          {/* Alerta duplicado */}
          {duplicado && !duplicadoIgnorado && (
            <div className={styles.duplicadoBox}>
              <strong>⚠️ Ya existe una visita este mes para este apartamento</strong>
              {duplicado.visitas.map(v => (
                <div key={v.id} className={styles.duplicadoItem}>
                  {new Date(v.fecha).toLocaleDateString('es-CO')} — {v.auditor} —
                  <span className={styles.duplicadoEstado}> {v.estado}</span>
                </div>
              ))}
              {duplicado.count >= 2 ? (
                <p className={styles.duplicadoBloqueo}>
                  Con {duplicado.count} visitas este mes no se permite crear otra. Contacta al administrador.
                </p>
              ) : (
                <div className={styles.duplicadoAcciones}>
                  <p className={styles.duplicadoTexto}>¿Deseas continuar de todas formas?</p>
                  <button className={styles.btnDuplicadoContinuar} onClick={async () => {
                    setDuplicadoIgnorado(true);
                    setDuplicado(null);
                    await handleNext(true);
                  }}>Sí, continuar</button>
                  <button className={styles.btnDuplicadoCancelar} onClick={() => setDuplicado(null)}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PASO 1: Medidores ─────────────────────────────────────── */}
      {step === 1 && (
        <div className={styles.section}>
          {/* Estado de progreso en medidores */}
          <div className={styles.meterProgress}>
            {['luz', 'agua', 'gas'].map(tipo => {
              const m = medidores[tipo];
              const done = m.sin_acceso || (m.lectura && m.lectura_guardada);
              return (
                <span key={tipo} className={`${styles.meterPill} ${done ? styles.meterPillDone : styles.meterPillPending}`}>
                  {tipo === 'luz' ? '💡' : tipo === 'agua' ? '💧' : '🔥'} {tipo}
                  {done ? ' ✓' : ''}
                </span>
              );
            })}
            <span className={styles.meterCount}>{meterDoneCount}/3</span>
          </div>

          {!online && (
            <p className={`${styles.hint} ${styles.hintOffline}`}>
              📵 Sin conexión — las fotos y lecturas se guardarán localmente y se enviarán al recuperar señal.
            </p>
          )}

          {medidoresPendientes.length > 0 && (
            <div className={styles.lecturaRequeridaAlert}>
              {medidoresPendientes.map(t => {
                const estado = estadoMedidor(t);
                const nombre = t === 'luz' ? '⚡ Luz' : t === 'agua' ? '💧 Agua' : '🔥 Gas';
                if (estado === 'pendiente')   return <span key={t}>{nombre}: falta foto o marcar sin evidencia</span>;
                if (estado === 'sinLectura')  return <span key={t}>{nombre}: escribe la lectura y presiona ✓</span>;
                return null;
              })}
            </div>
          )}

          {['luz', 'agua', 'gas'].map(tipo => (
            <MeterField
              key={tipo}
              tipo={tipo}
              data={medidores[tipo]}
              isOnline={online}
              lecturaRequerida={estadoMedidor(tipo) === 'sinLectura'}
              onChange={(field, val) => updateMedidor(tipo, field, val)}
              onFile={file => handleMedidorFile(tipo, file)}
            />
          ))}
        </div>
      )}

      {/* ── PASO 2: Observaciones ─────────────────────────────────── */}
      {step === 2 && (
        <div className={styles.section}>
          <div className={styles.field}>
            <label>Observaciones generales</label>
            <textarea
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              placeholder="Ej: Se solicitó llave al portero para acceso. Medidor de luz sin pantalla."
              rows={5}
            />
          </div>
          <div className={styles.summary}>
            <h3>Resumen</h3>
            {!online && <p className={styles.offlineNote}>📵 Se guardará localmente y sincronizará al recuperar señal</p>}
            <p><strong>Ciudad:</strong> {ciudades.find(c => String(c.id) === String(ciudadId))?.nombre}</p>
            <p><strong>Conjunto:</strong> {conjuntoNombre}</p>
            {torreId && <p><strong>Torre:</strong> {torreNombre}</p>}
            <p><strong>Apartamento:</strong> {apartamento}</p>
            <p><strong>GPS:</strong> {latitud ? `${latitud}, ${longitud}${gpsMode === 'last_known' ? ' (última conocida)' : ''}` : '⚠️ Sin GPS'}</p>
            <p><strong>Medidores:</strong> {
              ['luz','agua','gas']
                .filter(t => medidores[t].foto_path || medidores[t].foto_base64 || medidores[t].foto_file || medidores[t].lectura || medidores[t].sin_acceso)
                .map(t => medidores[t].sin_acceso ? `${t} (sin acceso)` : t)
                .join(', ') || 'Ninguno registrado'
            }</p>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Botón guardar progreso (aparece cuando hay borrador activo, paso 1 o 2) */}
      {currentDraftId && step >= 1 && (
        <div className={styles.saveDraftRow}>
          <button className={styles.btnSaveDraft} onClick={handleSaveDraft}>
            💾 Guardar progreso y continuar luego
          </button>
        </div>
      )}

      {/* Navegación */}
      <div className={styles.nav}>
        {step > 0 && !locationLocked && (
          <button className={styles.btnSecondary} onClick={() => setStep(s => s - 1)}>
            Anterior
          </button>
        )}
        {step > 0 && locationLocked && step > 1 && (
          <button className={styles.btnSecondary} onClick={() => setStep(s => s - 1)}>
            Anterior
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button className={styles.btnPrimary} onClick={handleNext} disabled={!canNext()}>
            Siguiente
          </button>
        ) : (
          <button className={styles.btnPrimary} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Guardando...' : online ? '✓ Guardar visita' : '📥 Guardar localmente'}
          </button>
        )}
      </div>
    </Layout>
  );
}
